import Zone from "@insite/client-framework/Components/Zone";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getProductListDataView } from "@insite/client-framework/Store/Pages/ProductList/ProductListSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import React, { FC } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    view: state.pages.productList.view,
    productsDataView: getProductListDataView(state),
});

type Props = ReturnType<typeof mapStateToProps> & WidgetProps;

export interface ProductListControlsContainerStyles {
    container?: GridContainerProps;
    countGridItem?: GridItemProps;
    countTableViewGridItem?: GridItemProps;
    sortGridItem?: GridItemProps;
    sortTableViewGridItem?: GridItemProps;
    filterGridItem?: GridItemProps;
    filterTableViewGridItem?: GridItemProps;
    viewSelectGridItem?: GridItemProps;
    viewSelectTableViewGridItem?: GridItemProps;
}

export const controlsContainerStyles: ProductListControlsContainerStyles = {
    container: {
        gap: 0,
    },
    countGridItem: {
        css: css`
            &.sort-by-wrap {
                flex-basis: 24%;
                max-width: 24%;
                [class*="FormFieldStyle"] {
                    width: 255px;
                }
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            css`
                                flex-basis: 50%;
                                max-width: 50%;
                                text-align: left;
                            `,
                        ],
                        "max",
                    )}
            }
        `,
    },
    countTableViewGridItem: {
        width: [12, 6, 1, 1, 3],

        css: css`
            @media screen and (min-width: 768px) and (max-width: 1160px) {
                flex-basis: 50%;
                max-width: 50%;
                text-align: left;
            }

            &.sort-by-wrap {
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            css`
                                flex-basis: 50%;
                                max-width: 50%;
                                text-align: left;

                                [class*="FormFieldStyle"] {
                                    width: 255px;
                                }
                            `,
                        ],
                        "max",
                    )}
            }
        `,
    },
    sortGridItem: {
        width: [12, 12, 5, 4, 2],
        css: css`
            &.per-page-wrap {
                flex-basis: 20%;
                max-width: 20%;
                [class*="PaginationStyle"] {
                    margin: 0;
                }
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            css`
                                flex-basis: 50%;
                                max-width: 50%;
                                text-align: left;

                                [class*="PerPageSelect"] {
                                    justify-content: flex-end;

                                    [class*="FormFieldStyle"] {
                                        padding-right: 0;
                                    }
                                }
                            `,
                        ],
                        "max",
                    )}

                @media screen and (min-width: 768px) and (max-width: 1160px) {
                    flex-basis: 42%;
                    max-width: 42%;

                    [class*="PerPageSelect"] {
                        margin: 0;
                    }
                }
            }
            &.filter-content-wrap {
                flex-basis: 15%;
                max-width: 15%;

                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            css`
                                display: none;
                            `,
                        ],
                        "max",
                    )}
            }
        `,
    },
    sortTableViewGridItem: {
        width: [12, 12, 5, 4, 4],

        css: css`
            &.per-page-wrap {
                flex-basis: 14%;
                max-width: 14%;
                [class*="PaginationStyle"] {
                    margin: 0;
                }
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            css`
                                flex-basis: 50%;
                                max-width: 50%;
                                text-align: left;

                                [class*="PerPageSelect"] {
                                    justify-content: flex-end;

                                    [class*="FormFieldStyle"] {
                                        padding-right: 0;
                                    }
                                }
                            `,
                        ],
                        "max",
                    )}

                @media screen and (min-width: 768px) and (max-width: 1160px) {
                    &.per-page-wrap {
                        flex-basis: 50%;
                        max-width: 50%;
                        text-align: left;

                        [class*="PerPageSelect"] {
                            justify-content: flex-end;

                            [class*="FormFieldStyle"] {
                                padding-right: 0;
                            }
                        }
                    }
                }
            }
            &.filter-content-wrap {
                flex-basis: 12%;
                max-width: 12%;

                @media screen and (min-width: 768px) and (max-width: 1160px) {
                    flex-basis: 20%;
                    max-width: 20%;
                    padding: 10px 0;
                }

                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            css`
                                display: none;
                            `,
                        ],
                        "max",
                    )}
            }
        `,
    },
    filterGridItem: {
        width: 0,
    },
    filterTableViewGridItem: {
        width: [0, 0, 2, 2, 2],

        css: css`
            &.grid-icon-wrap {
                flex-basis: 5%;
                max-width: 5%;
                svg {
                    height: 24px;
                    width: 24px;
                }
                > div > div {
                    padding-top: 2px;
                }

                @media screen and (min-width: 768px) and (max-width: 1160px) {
                    padding: 10px 0;
                }
            }
        `,
    },
    viewSelectGridItem: {
        width: [0, 0, 5, 3, 2],
        css: css`
            &.pagination-wrap {
                flex-basis: 41%;
                max-width: 41%;

                [class*="PaginationStyle"] {
                    margin: 0;
                }

                div.product-list-pagination {
                    text-align: center;
                    padding: 0;
                    border: none;
                    background: transparent;
                    box-shadow: none;
                    border-radius: unset;
                }

                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            css`
                                flex-basis: 100%;
                                max-width: 100%;
                                display: block;

                                nav {
                                    justify-content: end;
                                }
                            `,
                        ],
                        "max",
                    )}
                @media screen and (min-width: 768px) and (max-width: 1160px) {
                    flex-basis: 100%;
                    max-width: 100%;

                    nav {
                        justify-content: flex-end;
                    }
                }
            }

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        display: none;
                    `,
                    css`
                        display: none;
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    viewSelectTableViewGridItem: {
        width: [0, 0, 4, 5, 6],
        css: css`
            &.pagination-wrap {
                flex-basis: 42%;
                max-width: 42%;

                [class*="PaginationStyle"] {
                    margin: 0;
                }

                div.product-list-pagination {
                    text-align: center;
                    padding: 0;
                    border: none;
                    background: transparent;
                    box-shadow: none;
                    border-radius: unset;
                }

                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            css`
                                flex-basis: 100%;
                                max-width: 100%;
                                display: block;

                                nav {
                                    justify-content: end;
                                }
                            `,
                        ],
                        "max",
                    )}
                @media screen and (min-width: 768px) and (max-width: 1160px) {
                    flex-basis: 100%;
                    max-width: 100%;

                    nav {
                        justify-content: flex-end;
                    }
                }
            }

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        display: none;
                    `,
                    css`
                        display: none;
                    `,
                    null,
                    null,
                    null,
                ])}

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        display: none;
                    `,
                    css`
                        display: none;
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    },
};

const styles = controlsContainerStyles;
const customCssWrapper = {
    css: css`
        margin: 16px 0;
        text-align: center;
        padding: 12px 16px 6px;
        border: 1px solid #e0e0e0;
        background: #fff;
        box-shadow: inset 0 0 7px 0 rgba(0, 0, 0, 0.1);
        border-radius: 4px;

        .per-page-wrap,
        .sort-by-wrap {
            select ~ span:not([class*="IconWrapper"]) {
                display: none;
            }
            select {
                margin-bottom: 0 !important;
            }
            label {
                font-size: 14px;
                line-height: 18px;
                color: #959595;
                font-weight: 400;
                font-style: italic;
            }
        }
    `,
};
const ProductListControlsContainer: FC<Props> = ({ id, view, productsDataView }) => {
    if (!productsDataView.value) {
        return null;
    }
    const products = productsDataView.value;
    return (
        <>
            {products.length > 1 && (
                <GridContainer {...styles.container} {...customCssWrapper}>
                    <GridItem
                        className="sort-by-wrap"
                        {...(view === "Table" ? styles.countTableViewGridItem : styles.countGridItem)}
                    >
                        <Zone contentId={id} zoneName="Content00" />
                    </GridItem>
                    <GridItem
                        className="per-page-wrap"
                        {...(view === "Table" ? styles.sortTableViewGridItem : styles.sortGridItem)}
                    >
                        <Zone contentId={id} zoneName="Content01" />
                    </GridItem>
                    <GridItem
                        className="filter-content-wrap"
                        {...(view === "Table" ? styles.sortTableViewGridItem : styles.sortGridItem)}
                    >
                        <Zone contentId={id} zoneName="Content02" />
                    </GridItem>
                    <GridItem
                        className="grid-icon-wrap"
                        {...(view === "Table" ? styles.filterTableViewGridItem : styles.filterGridItem)}
                    >
                        <Zone contentId={id} zoneName="Content03" />
                    </GridItem>
                    <GridItem
                        className="pagination-wrap"
                        {...(view === "Table" ? styles.viewSelectTableViewGridItem : styles.viewSelectGridItem)}
                    >
                        <Zone contentId={id} zoneName="Content04" />
                    </GridItem>
                </GridContainer>
            )}
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(ProductListControlsContainer),
    definition: {
        group: "Product List",
        displayName: "List Controls Container",
        allowedContexts: ["ProductListPage"],
    },
};

export default widgetModule;
